'use client'

import Button from '@lyra/core/components/Button'
import { Settings } from '@lyra/core/components/Icon'
import Section, { SectionProps } from '@lyra/core/components/Section'
import TradeOptionSettingsModal from '@lyra/web/components/trade/TradeOptionSettingsModal'
import TradeOptionTable from '@lyra/web/components/trade/TradeOptionTable'
import TradeOptionTableExpiryButtonGroup from '@lyra/web/components/trade/TradeOptionTable/TradeOptionTableExpiryButtonGroup'
import { Instrument } from '@lyra/web/constants/instruments'
import { MarketId } from '@lyra/web/constants/markets'
import useOrderbookTimestamp from '@lyra/web/hooks/useOrderbookTimestamp'
import useSelectedExpiryWithDefault from '@lyra/web/hooks/useSelectedExpiryWithDefault'
import useTickers from '@lyra/web/hooks/useTickers'
import {
  getBoardFromTickers,
  getUniqueExpiries,
  parseOptionFromInstrumentName,
} from '@lyra/web/utils/instruments'
import React, { useCallback, useMemo, useState } from 'react'

type Props = {
  marketId: MarketId
  instruments: Instrument[]
} & SectionProps

export default function OptionsChainSection({ marketId, instruments, ...stackProps }: Props) {
  const utcServerTimestamp = useOrderbookTimestamp().getUtcTimestampSec()
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const expiries: Date[] = useMemo(() => getUniqueExpiries(instruments), [instruments])

  const [selectedExpiry, setSelectedExpiry] = useSelectedExpiryWithDefault(instruments)

  const expiryInstrumentNames = useMemo(
    () =>
      instruments
        .filter((instrument) => {
          const option = parseOptionFromInstrumentName(instrument.instrument_name)
          if (!option || !selectedExpiry) {
            return false
          }
          return selectedExpiry.getTime() === option.expiry.getTime()
        })
        .map((instrument) => instrument.instrument_name),
    [instruments, selectedExpiry]
  )
  const tickers = useTickers(expiryInstrumentNames)

  const board = useMemo(() => {
    if (selectedExpiry && tickers) {
      return getBoardFromTickers(selectedExpiry, Object.values(tickers), utcServerTimestamp)
    } else {
      return null
    }
  }, [selectedExpiry, tickers, utcServerTimestamp])

  // TODO: @earthtojake add "calls/puts" table back
  const optionTable = 'all'

  const handleSelectExpiry = useCallback(
    (expiry: Date) => {
      setSelectedExpiry(expiry)
    },
    [setSelectedExpiry]
  )

  if (!selectedExpiry) {
    return null
  }

  return (
    <Section {...stackProps}>
      <Section.XStack
        width="100%"
        overflow="scroll"
        justifyContent="space-between"
        $mobile={{ overflow: 'visible' }}
      >
        <TradeOptionTableExpiryButtonGroup
          expiries={expiries}
          selectedExpiry={selectedExpiry}
          onChangeExpiry={handleSelectExpiry}
        />
        <Button
          style={{ position: 'sticky', right: 0 }}
          marginLeft="auto"
          icon={<Settings />}
          isSelected={!!isModalOpen}
          onPress={() => {
            setIsModalOpen(true)
          }}
        />
        {isModalOpen ? <TradeOptionSettingsModal onClose={() => setIsModalOpen(false)} /> : null}
      </Section.XStack>
      {!board ? (
        <>
          <Section.Separator />
          <Section.Spinner flexGrow={1} flexShrink={1} />
        </>
      ) : (
        <Section.Table flexGrow={1} flexShrink={1}>
          <TradeOptionTable
            height="100%"
            width="100%"
            marketId={marketId}
            optionTable={optionTable}
            selectedBoard={board}
          />
        </Section.Table>
      )}
    </Section>
  )
}
